// ============================================
// UTILIDADES GENERALES
// ============================================

import dayjs from './dayjs';
import { formatDate } from './formatProjectDate';

const PROD_API_FALLBACK_URL = 'https://agrobyte.cl';
const LOCAL_API_FALLBACK_URL = 'http://localhost:3000';

function normalizeApiBaseUrl(value = '') {
  const resolved = String(value || '').trim();

  if (!resolved) {
    return '';
  }

  return resolved.replace(/\/+$/, '');
}

function getRuntimeEnvironment() {
  const env = String(
    process.env.VUE_APP_ENV ||
    process.env.VUE_APP_STAGE ||
    process.env.NODE_ENV ||
    ''
  ).trim().toLowerCase();

  if (env.includes('prod')) {
    return 'production';
  }

  if (env.includes('stag') || env.includes('stagin')) {
    return 'staging';
  }

  if (typeof window !== 'undefined') {
    const hostname = String(window.location.hostname || '').trim().toLowerCase();
    if (hostname === 'agrobyte.cl' || hostname === 'www.agrobyte.cl') {
      return 'production';
    }
  }

  return 'development';
}

function getCurrentOrigin() {
  return normalizeApiBaseUrl(
    typeof window !== 'undefined' ? window.location.origin : ''
  );
}

function isLocalHostname(hostname = '') {
  const normalizedHostname = String(hostname || '').trim().toLowerCase();
  return ['localhost', '127.0.0.1', '::1'].includes(normalizedHostname);
}

function isLocalApiUrl(url = '') {
  try {
    const parsedUrl = new URL(String(url));
    return isLocalHostname(parsedUrl.hostname);
  } catch {
    return false;
  }
}

function resolveApiBaseUrl() {
  const runtimeConfiguredUrl = normalizeApiBaseUrl(
    typeof window !== 'undefined' ? window.__HB_API_URL__ : ''
  );

  if (runtimeConfiguredUrl) {
    return runtimeConfiguredUrl;
  }

  const buildConfiguredUrl = normalizeApiBaseUrl(
    process.env.VUE_APP_API_URL ||
    process.env.VITE_API_URL ||
    ''
  );

  const currentOrigin = getCurrentOrigin();
  const currentHostname = typeof window !== 'undefined'
    ? String(window.location.hostname || '').trim().toLowerCase()
    : '';

  if (
    currentOrigin &&
    !isLocalHostname(currentHostname) &&
    isLocalApiUrl(buildConfiguredUrl)
  ) {
    return currentOrigin;
  }

  if (buildConfiguredUrl) {
    return buildConfiguredUrl;
  }

  const runtimeEnvironment = getRuntimeEnvironment();

  if (runtimeEnvironment === 'production') {
    return PROD_API_FALLBACK_URL;
  }

  return currentOrigin || LOCAL_API_FALLBACK_URL;
}

// API Base URL
export const API_BASE_URL = resolveApiBaseUrl();
const RECAPTCHA_SITE_KEY_FALLBACK = '';
let recaptchaConfigPromise = null;

function safeJsonObjectParse(value) {
  if (!value || typeof value !== 'string') {
    return {};
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeHostname(hostname) {
  return String(hostname || '')
    .trim()
    .toLowerCase();
}

function getRuntimeRecaptchaSiteKeyMap() {
  if (typeof window === 'undefined') {
    return {};
  }

  const runtimeMap = window.__HB_RECAPTCHA_SITE_KEYS__ || window.RECAPTCHA_SITE_KEYS || {};
  if (runtimeMap && typeof runtimeMap === 'object' && !Array.isArray(runtimeMap)) {
    return runtimeMap;
  }

  const metaMap = document
    .querySelector('meta[name="recaptcha-site-keys"]')
    ?.getAttribute('content');

  return safeJsonObjectParse(metaMap);
}

function getEnvRecaptchaSiteKeyMap() {
  return safeJsonObjectParse(
    process.env.VUE_APP_RECAPTCHA_SITE_KEYS ||
      process.env.VITE_RECAPTCHA_SITE_KEYS ||
      ''
  );
}

function getHostnameMappedSiteKey(hostname) {
  const normalizedHostname = normalizeHostname(hostname);
  if (!normalizedHostname) {
    return '';
  }

  const runtimeMap = getRuntimeRecaptchaSiteKeyMap();
  const runtimeSiteKey = String(runtimeMap[normalizedHostname] || '').trim();
  if (runtimeSiteKey) {
    return runtimeSiteKey;
  }

  const envMap = getEnvRecaptchaSiteKeyMap();
  return String(envMap[normalizedHostname] || '').trim();
}

export async function getRecaptchaConfig() {
  if (!recaptchaConfigPromise) {
    recaptchaConfigPromise = fetch(`${API_BASE_URL}/api/recaptcha-config`)
      .then(async (response) => {
        const contentType = response.headers.get('content-type') || '';

        if (!response.ok || !contentType.includes('application/json')) {
          throw new Error('No fue posible obtener la configuracion de reCAPTCHA');
        }

        const result = await response.json();
        const localSiteKey = getRecaptchaSiteKey();
        return {
          enabled: Boolean(result?.enabled ?? localSiteKey),
          siteKey: String(localSiteKey || result?.siteKey || '').trim()
        };
      })
      .catch(() => {
        const fallbackSiteKey = getRecaptchaSiteKey();
        return {
          enabled: Boolean(fallbackSiteKey),
          siteKey: fallbackSiteKey
        };
      });
  }

  return recaptchaConfigPromise;
}

export function getRecaptchaSiteKey() {
  if (typeof window !== 'undefined') {
    const hostnameSiteKey = getHostnameMappedSiteKey(window.location.hostname);
    if (hostnameSiteKey) {
      return hostnameSiteKey;
    }

    const runtimeKey =
      window.__HB_RECAPTCHA_SITE_KEY__ ||
      window.RECAPTCHA_SITE_KEY ||
      '';
    const resolvedRuntimeKey = String(runtimeKey || '').trim();

    if (resolvedRuntimeKey) {
      return resolvedRuntimeKey;
    }

    const metaKey = document
      .querySelector('meta[name="recaptcha-site-key"]')
      ?.getAttribute('content');
    const resolvedMetaKey = String(metaKey || '').trim();

    if (resolvedMetaKey) {
      return resolvedMetaKey;
    }
  }

  const envKey =
    process.env.VUE_APP_RECAPTCHA_SITE_KEY ||
    process.env.VITE_RECAPTCHA_SITE_KEY ||
    process.env.VUE_APP_SITE_KEY ||
    process.env.VITE_SITE_KEY ||
    '';

  if (envKey) {
    return String(envKey).trim();
  }

  return RECAPTCHA_SITE_KEY_FALLBACK;
}

export const RECAPTCHA_SITE_KEY = getRecaptchaSiteKey();

let recaptchaScriptPromise = null;
let recaptchaActiveSourceIndex = 0;
const RECAPTCHA_SCRIPT_TIMEOUT_MS = 10000;
const RECAPTCHA_SCRIPT_SOURCES = [
  'https://www.google.com/recaptcha/api.js',
  'https://www.recaptcha.net/recaptcha/api.js'
];

function waitForRecaptchaReady(timeoutMs = RECAPTCHA_SCRIPT_TIMEOUT_MS) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA solo esta disponible en el navegador'));
  }

  if (window.grecaptcha?.ready) {
    return Promise.resolve(window.grecaptcha);
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const checkRecaptcha = () => {
      if (window.grecaptcha?.ready) {
        resolve(window.grecaptcha);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('reCAPTCHA no termino de inicializarse'));
        return;
      }

      window.setTimeout(checkRecaptcha, 100);
    };

    checkRecaptcha();
  });
}

function buildRecaptchaScriptUrl(baseUrl, siteKey) {
  return `${baseUrl}?render=${encodeURIComponent(siteKey)}`;
}

function getExistingRecaptchaScript() {
  return document.querySelector('script[data-recaptcha="google-v3"]');
}

export function resetRecaptchaScript() {
  recaptchaScriptPromise = null;

  if (typeof window === 'undefined') {
    return;
  }

  const existingScript = getExistingRecaptchaScript();
  if (existingScript) {
    existingScript.remove();
  }

  try {
    delete window.grecaptcha;
  } catch {
    window.grecaptcha = undefined;
  }

  try {
    delete window.___grecaptcha_cfg;
  } catch {
    window.___grecaptcha_cfg = undefined;
  }
}

function loadRecaptchaScriptFromSource(script, baseUrl, siteKey) {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };

    const handleLoad = async () => {
      script.dataset.loaded = 'true';
      cleanup();

      try {
        const grecaptcha = await waitForRecaptchaReady();
        resolve(grecaptcha);
      } catch (error) {
        reject(error);
      }
    };

    const handleError = () => {
      cleanup();
      reject(new Error(`No se pudo cargar reCAPTCHA desde ${baseUrl}`));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!script.isConnected) {
      script.src = buildRecaptchaScriptUrl(baseUrl, siteKey);
      script.async = true;
      script.defer = true;
      script.dataset.recaptcha = 'google-v3';
      script.dataset.siteKey = siteKey;
      script.dataset.source = baseUrl;
      document.head.appendChild(script);
    } else if (script.dataset.loaded === 'true') {
      handleLoad();
    }
  });
}

export function loadRecaptchaScript(siteKey, options = {}) {
  const resolvedSiteKey = String(siteKey || getRecaptchaSiteKey()).trim();
  const forceReload = Boolean(options?.forceReload);
  const useNextSource = Boolean(options?.useNextSource);

  if (!resolvedSiteKey) {
    return Promise.reject(new Error('No se encontro la site key de reCAPTCHA'));
  }

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA solo esta disponible en el navegador'));
  }

  if (forceReload) {
    resetRecaptchaScript();
  }

  if (window.grecaptcha?.ready) {
    return Promise.resolve(window.grecaptcha);
  }

  if (recaptchaScriptPromise) {
    return recaptchaScriptPromise;
  }

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    const existingScript = getExistingRecaptchaScript();
    if (existingScript) {
      const existingSiteKey = existingScript.dataset.siteKey || '';
      const existingSource = existingScript.dataset.source || RECAPTCHA_SCRIPT_SOURCES[recaptchaActiveSourceIndex];

      if (existingSiteKey && existingSiteKey !== resolvedSiteKey) {
        existingScript.remove();
        recaptchaScriptPromise = null;
        loadRecaptchaScript(resolvedSiteKey, options).then(resolve).catch(reject);
        return;
      }

      loadRecaptchaScriptFromSource(
        existingScript,
        existingSource,
        resolvedSiteKey
      ).then((grecaptcha) => {
        recaptchaActiveSourceIndex = Math.max(RECAPTCHA_SCRIPT_SOURCES.indexOf(existingSource), 0);
        resolve(grecaptcha);
      }).catch((error) => {
        recaptchaScriptPromise = null;
        reject(error);
      });
      return;
    }

    const initialSourceIndex = useNextSource
      ? (recaptchaActiveSourceIndex + 1) % RECAPTCHA_SCRIPT_SOURCES.length
      : recaptchaActiveSourceIndex;

    const tryNextSource = (sourceIndex = initialSourceIndex, attempts = 0) => {
      if (attempts >= RECAPTCHA_SCRIPT_SOURCES.length) {
        recaptchaScriptPromise = null;
        reject(new Error('No se pudo cargar reCAPTCHA'));
        return;
      }

      if (sourceIndex >= RECAPTCHA_SCRIPT_SOURCES.length) {
        tryNextSource(0, attempts);
        return;
      }

      const currentScript = getExistingRecaptchaScript() || document.createElement('script');

      loadRecaptchaScriptFromSource(currentScript, RECAPTCHA_SCRIPT_SOURCES[sourceIndex], resolvedSiteKey)
        .then((grecaptcha) => {
          recaptchaActiveSourceIndex = sourceIndex;
          resolve(grecaptcha);
        })
        .catch(() => {
          currentScript.remove();
          tryNextSource(sourceIndex + 1, attempts + 1);
        });
    };

    tryNextSource(initialSourceIndex, 0);
  });

  return recaptchaScriptPromise;
}

export async function executeRecaptcha(action = 'submit', siteKey, options = {}) {
  const resolvedSiteKey = String(siteKey || getRecaptchaSiteKey()).trim();
  const grecaptcha = await loadRecaptchaScript(resolvedSiteKey, options);

  return new Promise((resolve, reject) => {
    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(resolvedSiteKey, { action });
        resolve(token);
      } catch (error) {
        reject(error);
      }
    });
  });
}

export function validateDate(dateStart, dateEnd) {
  if (dayjs(dateStart).isBefore(dayjs(dateEnd))) {
    return true;
  } else if (dayjs(dateStart).isAfter(dayjs(dateEnd))) {
    toasted('La Fecha Inicial es mayor a la Fecha Final', 'danger');
    return false;
  } else {
    return true;
  }
}

export function toasted(mensaje, type = 'dark') {
  const typeMap = {
    success: 'success',
    ok: 'success',
    error: 'danger',
    danger: 'danger',
    warning: 'warning',
    advertencia: 'warning',
    info: 'info',
    primary: 'primary',
    secondary: 'secondary',
    dark: 'dark',
    light: 'light'
  };

  const bootstrapType = typeMap[type] || 'dark';

  // El mensaje se inserta como TEXTO (no HTML) para evitar XSS con datos del servidor
  const alertHtml = `
    <div class="alert alert-${bootstrapType} alert-dismissible fade show m-0 mb-1 px-3 py-2 fw-semibold" role="alert">
      <span class="toasted-message"></span>
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    </div>
  `;

  // Crear elemento temporal
  const container = document.createElement('div');
  container.className = 'position-fixed top-0 start-50 translate-middle-x mt-3';
  container.style.zIndex = '9999';
  container.innerHTML = alertHtml;
  container.querySelector('.toasted-message').textContent = mensaje;
  document.body.appendChild(container);

  // Remover después de 3 segundos
  setTimeout(() => {
    container.remove();
  }, 3000);
}

// formattNumber() ELIMINADO: formateaba con 'es-ES' fijo (toFixed(2) +
// toLocaleString para los miles + concatenar la coma). Nadie lo importaba —
// Home.vue tiene un método propio del mismo nombre que sí usa el Formato
// Regional—, pero seguía ahí como trampa para quien buscara "un formateador".
// Usar formatNumber() de utils/formatProjectData.js.

export const capitalizeLetter = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export function toUpperCase(str) {
  return str.toUpperCase();
}

// Las tres funciones de abajo delegan en utils/formatProjectDate.js, que elige
// el patrón según el Formato Regional del proyecto. Antes tenían el patrón
// incrustado ('DD/MM/YYYY', 'DD-MM-YYYY hh:mm a'), así que un proyecto en
// en-US seguía viendo el orden día/mes chileno.
//
// `formattDate` además armaba la fecha concatenando getDate()/getMonth() a
// mano, con lo que el orden DD/MM quedaba escrito en el código y no en un
// formato configurable. Había TRES copias de esa función (helpers.js,
// utils.js y Home.vue).
export function formattDate(fechaISO = '') {
  return formatDate(fechaISO === '' ? new Date() : fechaISO, 'date', { fallback: '' });
}

// CORRIGE UN BUG PREEXISTENTE: usaba 'hh:mm a' + un .replace('am','a.m.')
// hecho a mano (que además sólo funciona en español). Ahora es 24h, igual que
// el resto de la app.
export function formattDateHora(fechaStr) {
  return formatDate(fechaStr, 'stamp', { fallback: fechaStr });
}

// CORRIGE UN BUG PREEXISTENTE: formateaba con 'hh:mm' (reloj de 12) pero SIN
// AM/PM, así que las 09:30 y las 21:30 se mostraban idénticas.
export function formattDateHoraSinAMPM(fechaStr) {
  return formatDate(fechaStr, 'stamp', { fallback: fechaStr });
}

export function normalizarUrl(url) {
  if (!url) return '/';
  return url.startsWith('/') ? url : '/' + url;
}

export function normalizarUrlNoAdministracion(url) {
  return url.replace(/administracion\//g, '');
}

export function compareProyect(proyectId = 11) {
  if (sessionStorage.getItem('proyectSelected') == proyectId) {
    return true;
  }
  return false;
}

export function safeJsonParse(value, fallback = null) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

export function safeParseStorage(storage, key, fallback = null) {
  if (!storage || !key) {
    return fallback;
  }

  return safeJsonParse(storage.getItem(key), fallback);
}

// ============================================
// UTILIDADES DE MENÚ Y NOTIFICACIONES
// ============================================

export function prepareAlertsData(data) {
  const cantidadAlertas = (data || []).filter((alert) => alert.isRead === false).length;

  return {
    cantidadAlertas: cantidadAlertas,
    alertasSistema: [...(data || [])]
  };
}

export function clearNotificationState() {
  const PREFIX_SESSION = 'alerts:';
  const HB_PREFIXES = ['hb:', 'hb-', 'hbauth-', 'hblast-'];

  const matchHB = (k) => HB_PREFIXES.some((p) => k.startsWith(p));
  const clearBy = (storage, predicate) => {
    for (let i = storage.length - 1; i >= 0; i--) {
      const k = storage.key(i);
      if (k && predicate(k)) {
        storage.removeItem(k);
      }
    }
  };

  clearBy(sessionStorage, (k) => k.startsWith(PREFIX_SESSION) || matchHB(k));
  clearBy(localStorage, matchHB);

  sessionStorage.removeItem('notifications');
}

export const DEFAULT_API_TIMEOUT_MS = 15000;

function buildTimeoutError(timeoutMs) {
  const error = new Error(`La solicitud excedio el tiempo de espera (${timeoutMs} ms)`);
  error.name = 'AbortError';
  error.code = 'REQUEST_TIMEOUT';
  return error;
}

function mergeAbortSignals(signals = []) {
  const activeSignals = signals.filter(Boolean);
  if (!activeSignals.length) return undefined;
  if (activeSignals.length === 1) return activeSignals[0];

  const controller = new AbortController();
  const abort = () => {
    if (!controller.signal.aborted) {
      controller.abort();
    }
  };

  activeSignals.forEach((signal) => {
    if (signal.aborted) {
      abort();
      return;
    }
    signal.addEventListener('abort', abort, { once: true });
  });

  return controller.signal;
}

export async function fetchWithTimeout(url, options = {}) {
  const timeoutMs = Number(options.timeoutMs ?? DEFAULT_API_TIMEOUT_MS);
  const { timeoutMs: _timeoutMs, signal, ...fetchOptions } = options;

  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return fetch(url, { ...fetchOptions, signal });
  }

  const timeoutController = new AbortController();
  const timer = setTimeout(() => {
    timeoutController.abort(buildTimeoutError(timeoutMs));
  }, timeoutMs);

  try {
    const mergedSignal = mergeAbortSignals([signal, timeoutController.signal]);
    return await fetch(url, { ...fetchOptions, signal: mergedSignal });
  } catch (error) {
    if (error?.name === 'AbortError' && timeoutController.signal.aborted) {
      throw timeoutController.signal.reason || buildTimeoutError(timeoutMs);
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchAlertsCustom(proyect_id) {
  try {
    const response = await fetchWithToken(`/api/list-alerts-custom?proyecto_id=${proyect_id}`, {
      method: 'GET',
      timeoutMs: 30000
    });
    const data = await response.json();
    return data;
  } catch (err) {
    if (err?.code === 'REQUEST_TIMEOUT' || err?.name === 'AbortError') {
      console.warn('La carga de alertas excedio el tiempo de espera.');
      return null;
    }
    console.error('Error al cargar alertas:', err);
    return null;
  }
}

// ============================================
// VALIDACIÓN DE RUTAS CON ACCESO
// ============================================

export function validarRutaConAcceso(pathActual, queryParams) {
  const pathLower = pathActual.toLowerCase();
  const menu = safeParseStorage(sessionStorage, 'menu', {});
  if (!menu) return { permitido: false, motivo: 'no-found' };

  const secciones = Object.keys(menu);

  for (const seccion of secciones) {
    const modulos = menu[seccion]?.sub_modulos || [];
    for (const modulo of modulos) {
      const url = modulo.url?.replace(/^\//, '');
      if (!url) continue;

      const [moduloPath, rawParams] = url.split('?');
      const rutaEsperada = `/${moduloPath}`.toLowerCase();
      const paramsEsperados = new URLSearchParams(rawParams || '');
      const clave = [...paramsEsperados.keys()][0];

      if (rutaEsperada === pathLower && clave && queryParams[clave] !== undefined) {
        const valorEsperado = paramsEsperados.get(clave);
        const valorActual = queryParams[clave];

        if (valorEsperado == valorActual) {
          if (modulo.accesos === false) {
            return { permitido: false, motivo: 'no-authorization' };
          } else {
            return { permitido: true };
          }
        }
      }
    }
  }

  return { permitido: false, motivo: 'no-found' };
}
// ============================================
// FETCH CON TOKEN AUTOMÁTICO
// ============================================

// Manejo global de sesión expirada: ante un 401 con token presente se hace
// logout una sola vez (evita loops y cascadas de toasts si fallan varias
// llamadas en paralelo). Import dinámico de auth para no crear ciclo
// (auth.js importa de helpers.js).
let sesionExpiradaEnCurso = false;

async function handleUnauthorized() {
  if (sesionExpiradaEnCurso) return;
  sesionExpiradaEnCurso = true;

  toasted('Tu sesión expiró. Inicia sesión nuevamente.', 'warning');

  try {
    const { performLogout } = await import('./auth');
    // Pequeña espera para que el usuario alcance a ver el aviso
    setTimeout(() => performLogout({ redirectTo: '/login', broadcast: true }), 800);
  } catch {
    sessionStorage.clear();
    setTimeout(() => { window.location.href = '/login'; }, 800);
  }
}

export async function fetchWithToken(url, options = {}) {
  const token = sessionStorage.getItem('token');

  // Si la URL comienza con /api/, agregar el base URL
  const finalUrl = url.startsWith('/api/') ? `${API_BASE_URL}${url}` : url;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (token) {
    headers['Authorization'] = token;
  }

  const finalOptions = {
    ...options,
    headers
  };

  const response = await fetchWithTimeout(finalUrl, finalOptions);

  // 401 fuera del login = sesión expirada, inválida o ausente. El login con
  // credenciales incorrectas ocurre en /login (excluido), así que no hay loops.
  if (response.status === 401 && window.location.pathname !== '/login') {
    handleUnauthorized();
  }

  return response;
}

export async function fetchJsonWithToken(url, options = {}) {
  const response = await fetchWithToken(url, options);
  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : null;

  if (!response.ok) {
    const error = new Error(payload?.message || `HTTP ${response.status}`);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload ?? {};
}

// ============================================
// FUNCIONES ADICIONALES DEL CDN
// ============================================

/**
 * Convierte un valor a número soportando múltiples formatos
 * @param {*} v - Valor a convertir
 * @returns {number} Número o NaN
 */
export const toNumberFlexible = (v) => {
  if (typeof v === 'number') return v;
  const s = String(v).trim()
    .replace(/\s/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '') // puntos como miles
    .replace(/,(?=\d{3}(\D|$))/g, '') // comas como miles
    .replace(',', '.'); // decimal a punto
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Verifica si un valor se parece a un número
 * @param {*} v - Valor a verificar
 * @returns {boolean} true si el valor es numérico
 */
export const isNumericLike = (v) => {
  if (v == null || v === '') return false;
  if (typeof v === 'number') return Number.isFinite(v);

  const s = String(v).trim();
  if (!s) return false;

  const normalized = s
    .replace(/\s/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')   // puntos como miles
    .replace(/,(?=\d{3}(\D|$))/g, '')    // comas como miles
    .replace(',', '.');                  // decimal a punto
  const n = Number(normalized);
  return Number.isFinite(n);
};

// formatEsCL() ELIMINADO: tenía 'es-CL' incrustado en el nombre y en la
// implementación. Nadie lo importaba. Usar formatNumber() de
// utils/formatProjectData.js con { grouping: false, maxDecimals: n }.

/**
 * Calcula el tiempo transcurrido entre una fecha y ahora
 * @param {string} fechaStr - Fecha en formato ISO
 * @returns {string} Tiempo transcurrido (ej: "Hace 5 minutos")
 */
export function afterBeforeTime(fechaStr) {
  const ahora = new Date();
  const fecha = new Date(fechaStr.replace(' ', 'T'));

  const diffMs = ahora - fecha;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHoras = Math.floor(diffMin / 60);
  const diffDias = Math.floor(diffHoras / 24);
  const diffSemanas = Math.floor(diffDias / 7);
  const diffMeses = Math.floor(diffDias / 30);

  if (diffMin < 1) return 'Hace 1 min';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  if (diffHoras < 24) return `Hace ${diffHoras} hora${diffHoras > 1 ? 's' : ''}`;
  if (diffDias < 7) return `Hace ${diffDias} día${diffDias > 1 ? 's' : ''}`;
  if (diffSemanas < 4) return `Hace ${diffSemanas} semana${diffSemanas > 1 ? 's' : ''}`;
  if (diffMeses <= 3) return `Hace ${diffMeses} mes${diffMeses > 1 ? 'es' : ''}`;

  return fechaStr;
}
